import { CHARTER_QUESTIONS } from '../charter'
import { TASK_TEXT } from './text'
import type { BuiltInTaskDefinition } from './types'

export const COO_ONBOARD_CEO_TASK: BuiltInTaskDefinition = {
    slug: 'coo-onboard-ceo',
    name: 'Onboard a new CEO',
    kind: 'llm',
    overlapPolicy: 'skip',
    concurrencySafe: false,
    serializeOn: null,
    builtInRole: 'onboarding',
    chatPurpose: null,
    goal: 'A new CEO’s first week ends with their first team chartered, its first specialist and that specialist’s first task proposed with default gates and a spend cap, approved by the CEO as one plan, and the first run of that task started and its outcome reported',
    metric: {
        text: 'Share of onboardings that reach a first run of the first specialist’s task, and days from the first answer to that run',
    },
    guardrails: [
        'Propose only: write the whole org shape as one plan with os.write_plan; the OS applies it only when the CEO approves',
        'Ask the CEO once, with os.ask_ceo, after the plan is written',
        'The drafted charter answers every charter question; a gap is your marked draft, never left out',
        'One team and one first specialist: the charter’s roles answer is what the team grows into, not a hire list for today',
        'Every proposed task declares its gates and a spend cap; anything that spends, publishes or emails outside the enterprise is gated on the CEO',
        'Never report an item or a run as done unless the plan or the run says so',
    ],
    inputs: [
        {
            key: 'request',
            label: 'What the CEO has said about the company and its first team',
            type: 'string',
            required: true,
        },
        ...CHARTER_QUESTIONS.map(question => ({ ...question, type: 'string' as const, required: false })),
        {
            key: 'ceoNote',
            label: 'CEO note from a previous round',
            type: 'string',
            required: false,
        },
        {
            key: 'chatSessionId',
            label: 'Chat session this plan belongs to (chat fills it)',
            type: 'string',
            required: false,
        },
        {
            key: 'planKey',
            label: 'The conversation this plan belongs to when it has no chat session (the OS fills it)',
            type: 'string',
            required: false,
        },
    ],
    tools: [
        'os.read_enterprise',
        'os.list_tool_bindings',
        'os.search_catalog',
        'os.write_plan',
        'os.read_plan',
        'os.ask_ceo',
    ],
    gates: [
        {
            tool: 'os.ask_ceo',
            owner: 'ceo',
            approval: 'specific',
        },
    ],
    spend: {
        moneyUsdPerRun: 2,
        maxModelTier: 'mid',
    },
    workspace: null,
    requiredWrites: null,
    allowSelfReaction: null,
    contextPrefixes: null,
    trainGrants: null,
    finishesOn: null,
    successors: [
        {
            slug: 'coo-onboard-ceo',
            on: 'ceo_approved',
            resumeConversation: true,
        },
        {
            slug: 'coo-onboard-ceo',
            on: 'ceo_changes_requested',
        },
        {
            slug: 'task-self-improvement',
            on: 'terminal',
        },
    ],
    skillMd: TASK_TEXT['coo-onboard-ceo.md'].replace(
        '{{charterQuestions}}',
        CHARTER_QUESTIONS.map(question => `- \`${question.key}\`: ${question.label}`).join('\n'),
    ),
    adapterMd: TASK_TEXT['coo-onboard-ceo.adapter.md'],
}
